import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  CMSTag,
  TagListFilters,
  TagListResponse,
  TagUsageSummary,
} from '@/client/types/cms';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

const TAGS = '/api/admin/tags';
const ENTITIES = '/api/admin/entities';

/** Stable query keys — keep `cms-admin`/`tags` prefix for existing invalidation. */
export const tagKeys = {
  all: ['cms-admin', 'tags'] as const,
  list: (q?: object) => [...tagKeys.all, 'list', q ?? {}] as const,
  detail: (tagid: number) => [...tagKeys.all, 'detail', tagid] as const,
  usage: (tagid: number) => [...tagKeys.all, 'usage', tagid] as const,
  entities: (tagid: number, q?: object) => [...tagKeys.all, 'entities', tagid, q ?? {}] as const,
  cms: (tagid: number, q?: object) => [...tagKeys.all, 'cms', tagid, q ?? {}] as const,
  entityTags: (eid: number) => [...tagKeys.all, 'entity', eid] as const,
};

const qs = (params: Record<string, unknown>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

const unwrapEnvelope = (raw: any): any => {
  if (raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw) {
    return raw.data;
  }
  return raw;
};

const coercePagination = (raw: any): TagListResponse['pagination'] => {
  const candidates = [
    raw?.pagination,
    raw?.data?.pagination,
    raw?.data?.data?.pagination,
  ];
  const pagination = candidates.find((p) => p && typeof p === 'object') ?? undefined;
  if (!pagination) return undefined;
  const totalRaw = pagination.total ?? pagination.total_count;
  const total = totalRaw == null || totalRaw === '' ? undefined : Number(totalRaw);
  return {
    ...pagination,
    limit: pagination.limit != null ? Number(pagination.limit) : undefined,
    offset: pagination.offset != null ? Number(pagination.offset) : undefined,
    count: pagination.count != null ? Number(pagination.count) : undefined,
    // Only keep total when it is a real finite number — never treat page `count` as catalog size.
    total: Number.isFinite(total) ? total : undefined,
  };
};

const unwrapList = (raw: any): any[] => {
  const payload = unwrapEnvelope(raw);
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tags)) return payload.tags;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

export const useAdminTagsQuery = (params: TagListFilters = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: tagKeys.list(params),
    queryFn: withErrorHandling(async (): Promise<TagListResponse> => {
      const raw = await request<any>(
        `${TAGS}${qs({ limit: 50, include_usage: true, include_inactive: true, ...params })}`,
      );
      const data = unwrapEnvelope(raw) ?? raw;
      return {
        tags: (data?.tags ?? unwrapList(raw)) as CMSTag[],
        pagination: coercePagination(data) ?? coercePagination(raw),
      };
    }, 'Failed to fetch tags'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useAdminTagDetailQuery = (
  tagid: number | null,
  opts: { include_usage?: boolean } = {},
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: tagKeys.detail(tagid ?? 0),
    queryFn: withErrorHandling(async (): Promise<CMSTag> => {
      const raw = await request<any>(
        `${TAGS}/${tagid}${qs({ include_usage: opts.include_usage ?? true })}`,
      );
      return (unwrapEnvelope(raw) ?? raw) as CMSTag;
    }, 'Failed to fetch tag'),
    enabled: typeof tagid === 'number' && tagid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useAdminTagUsageQuery = (tagid: number | null) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: tagKeys.usage(tagid ?? 0),
    queryFn: withErrorHandling(async (): Promise<TagUsageSummary> => {
      const raw = await request<any>(`${TAGS}/${tagid}/usage`);
      return (unwrapEnvelope(raw) ?? raw) as TagUsageSummary;
    }, 'Failed to fetch tag usage'),
    enabled: typeof tagid === 'number' && tagid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useAdminTagEntitiesQuery = (
  tagid: number | null,
  params: { q?: string; limit?: number; offset?: number } = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: tagKeys.entities(tagid ?? 0, params),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${TAGS}/${tagid}/entities${qs(params)}`);
      const data = unwrapEnvelope(raw) ?? raw;
      return {
        entities: (data?.entities ?? []) as Array<Record<string, unknown>>,
        pagination: data?.pagination,
      };
    }, 'Failed to fetch tagged entities'),
    enabled: enabled && typeof tagid === 'number' && tagid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useAdminTagCmsQuery = (
  tagid: number | null,
  params: { q?: string; limit?: number; offset?: number } = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: tagKeys.cms(tagid ?? 0, params),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${TAGS}/${tagid}/cms${qs(params)}`);
      const data = unwrapEnvelope(raw) ?? raw;
      return {
        cms_pages: (data?.cms_pages ?? []) as Array<Record<string, unknown>>,
        pagination: data?.pagination,
      };
    }, 'Failed to fetch tagged CMS pages'),
    enabled: enabled && typeof tagid === 'number' && tagid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useEntityTagsQuery = (eid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: tagKeys.entityTags(eid ?? 0),
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`${ENTITIES}/${eid}/tags`);
      const data = unwrapEnvelope(raw) ?? raw;
      return {
        eid: data?.eid ?? eid,
        tagids: (data?.tagids ?? []) as number[],
        tags: (data?.tags ?? []) as CMSTag[],
      };
    }, 'Failed to fetch entity tags'),
    enabled: enabled && typeof eid === 'number' && eid > 0,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
